import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormInstanceType, FormStatuses } from 'types/formService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { requestCompanyFormInstance, requestSetAnswers } from 'api/operations/formService';
import { createContext, useContext } from 'react';
import { repeatRequestFor } from 'global/utils/repeatRequestFor';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms/dynamic-forms/DynamicFormDelegate';
import { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { EnrollmentHeaderSafeguardDelegate } from 'store/flows/enrollment/enrollmentHeaderSafeguardDelegate';
import { bpHistory } from 'global/bpHistory';

export default class CompanyInfoFormPageStore {
  private _rootStore = getRootStoreInstance();
  private _domainData: EnrollmentFlowDomainParams;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _isInitialDataLoading = false;
  private _generationFailed = false; // A flag for handling a rare bug with the form generation.
  private _formInstance: FormInstanceType | null = null;

  private _createFormDelegate(formObject: FormObjectFromJSON): void {
    this.dynamicFormDelegate = new DynamicFormDelegate({
      currentUserRoles: this._rootStore.userStore.userRoles,
      fetchForm: async (): Promise<FormObjectFromJSON> => {
        return new Promise((resolve) => {
          resolve(formObject);
        });
      },
      onSubmit: this.completeForm.bind(this),
    });
  }

  public dynamicFormDelegate: DynamicFormDelegate | null = null;
  public safeguardDelegate = new EnrollmentHeaderSafeguardDelegate({
    onSaveChanges: this.saveFormChanges.bind(this),
    onNoSavingExit: this.onBackRedirect.bind(this),
    isStepCompleted: false,
    enrollmentPeriodModule: this._enrollmentPeriodModule,
  });

  get enrollmentPeriodModule(): EnrollmentPeriodModule {
    return this._enrollmentPeriodModule;
  }

  get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  get generationFailed(): boolean {
    return this._generationFailed;
  }

  get isFormCompleted(): boolean {
    return this._formInstance?.status === FormStatuses.COMPLETE;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFormInstance(): Promise<void> {
    // Repeat the request every 2 seconds during 10 seconds time span. After that return null.
    const res = await repeatRequestFor(
      () => requestCompanyFormInstance(this._domainData.periodId),
      (data: unknown): boolean => (data as FormInstanceType)?.id !== undefined,
    );
    runInAction(() => {
      if (res !== null) {
        this._createFormDelegate(res.form);
        this._formInstance = res;
        void this.dynamicFormDelegate!.buildForm(res.id, res.answers.common as Record<string, string>);
        this.safeguardDelegate.setIsStepCompleted(res.status === FormStatuses.COMPLETE);
      } else {
        this._generationFailed = true;
      }
    });
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.periodId),
      this.getFormInstance(),
    ]);
    runInAction(() => {
      this.setIsInitialDataLoading(false);
    });
  }

  public async completeForm(): Promise<void> {
    if (this.dynamicFormDelegate) {
      await requestSetAnswers({
        formInstanceId: this._formInstance!.id,
        answers: { common: this.dynamicFormDelegate.formAnswers, custom: {} },
        complete: true,
      });
      await runInAction(async () => {
        await requestApplyUserAction(this._domainData.periodId, USER_ACTIONS.COMPLETE_COMPANY_INFO);
      });
      bpHistory.push(`/checklist/${this._domainData.periodId}`);
    }
  }

  public async saveFormChanges(): Promise<void> {
    if (this.dynamicFormDelegate) {
      this.safeguardDelegate.saveChangesModalDelegate.close();
      this.dynamicFormDelegate.formController?.setLoading(true);
      await requestSetAnswers({
        formInstanceId: this._formInstance!.id,
        answers: { common: this.dynamicFormDelegate.formAnswers, custom: {} },
        complete: false,
      });
      runInAction(() => {
        this.dynamicFormDelegate!.formController?.setLoading(false);
      });
      bpHistory.push(`/checklist/${this._domainData.periodId}`);
    }
  }

  public onBackRedirect(): void {
    bpHistory.push(`/checklist/${this._domainData.periodId}`);
  }

  constructor(domainParams: EnrollmentFlowDomainParams) {
    this._domainData = domainParams;

    makeObservable<
      CompanyInfoFormPageStore,
      | '_isInitialDataLoading'
      | '_generationFailed'
      | 'dynamicFormDelegate'
      | 'safeguardDelegate'
      | 'enrollmentPeriodModule'
    >(this, {
      _isInitialDataLoading: observable,
      _generationFailed: observable,
      dynamicFormDelegate: observable,
      safeguardDelegate: observable,
      enrollmentPeriodModule: computed,
      isInitialDataLoading: computed,
      generationFailed: computed,
      setIsInitialDataLoading: action.bound,
      getFormInstance: action.bound,
      getInitialData: action.bound,
      completeForm: action.bound,
      saveFormChanges: action.bound,
      onBackRedirect: action.bound,
    });
  }
}

export const CompanyInfoFormContext = createContext(new CompanyInfoFormPageStore({} as EnrollmentFlowDomainParams));

export const useCompanyInfoFormStore = (): CompanyInfoFormPageStore => {
  return useContext(CompanyInfoFormContext);
};
